import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AgentVoiceCanonical } from '@leanopx/shared';

/**
 * Portal UI state — persists user preferences and shell layout across
 * sessions via Zustand's `persist` middleware. The localStorage key is
 * `leanopx-ui-prefs`; bump `version` whenever the persisted shape changes
 * (and write a migrator).
 *
 * v3 (portal redesign §3.2):
 *   - density type widened from 2-step (`comfortable|compact`) to 3-step
 *     (`compact|regular|comfy`); legacy `comfortable` migrates to `regular`.
 *   - default theme flipped from `daylight` to `midnight` (dark-default
 *     portal — see §5.2).
 *   - new sidebar pin / rail state: `sidePinned`, `railOpen`, `railWidth`,
 *     `railTab`. The legacy `sidebarCollapsed` is retained as a derived
 *     mirror of `!sidePinned` so the existing app-shell call sites keep
 *     compiling during the rewrite.
 *
 * v4 (redesign-v4 DS-1):
 *   - new `typeScale: 'base' | 'plus1' | 'plus2'` axis. `plus1` is the
 *     v4 default (+1 step on every `--text-*` token); legacy users
 *     missing the key migrate to `plus1` so the new bump is visible
 *     immediately. `base` is reachable in one click from /admin/settings
 *     for anyone who prefers the v3 scale.
 *   - `applyUiToDom` now writes `data-theme` + `data-type-scale` on
 *     <html> alongside the existing `.midnight` / `.daylight` classes.
 *     CSS selectors in `apps/web/src/styles/index.css` match either
 *     form so the v4 design-system primitives can use the attribute
 *     selectors the prototype shipped with.
 *
 * v6 (issue #114 Phase A):
 *   - `typeScale` default flipped from `plus1` to `plus2` (the v6
 *     `TWEAK_DEFAULTS.typeScale` from the design bundle). Legacy users
 *     persisted on `plus1` stay on `plus1`; only fresh visitors get
 *     `plus2`.
 *   - new `darkPalette: 'navy' | 'forest' | 'charcoal' | 'slate'` axis.
 *     `navy` is the v6 default (lives in `:root` in
 *     `packages/design-system/src/tokens/base.css`); the other three
 *     are opt-in via `[data-dark-palette='…']` blocks. The runtime
 *     only writes `data-dark-palette` when the user opts off `navy`,
 *     so `:root` always wins for default visitors.
 *   - new `lightPalette: 'sharp' | 'cream'` axis. `sharp` is the v6
 *     "Classic Lift" default (lives in `[data-theme='daylight']`);
 *     `cream` is the v5 paperback palette kept as opt-in via
 *     `[data-theme='daylight'][data-light-palette='cream']`. Same
 *     "only write when non-default" pattern as `darkPalette`.
 */

// Closed string sets — single source of truth. CLAUDE.md: closed sets
// live as one constant array, not two parallel literals. Types derive
// via `typeof <ARRAY>[number]` so adding a value means editing one
// line. Consumers (the SettingChoice options in /admin/settings) read
// the array directly; nothing hand-types the same literals twice.
export const THEMES = ['midnight', 'daylight', 'system'] as const;
export const DENSITIES = ['compact', 'regular', 'comfy'] as const;
export const TYPE_SCALES = ['base', 'plus1', 'plus2'] as const;
// v6 dark palettes — `navy` is the default and lives in `:root`. The
// other three are opt-in CSS blocks in `base.css`. Order here drives
// the SettingChoice option order in /admin/settings.
export const DARK_PALETTES = ['navy', 'forest', 'charcoal', 'slate'] as const;
// v6 light palettes — `sharp` (Classic Lift) is the default, `cream`
// is the v5 paperback opt-in.
export const LIGHT_PALETTES = ['sharp', 'cream'] as const;
export const RAIL_TABS = ['status', 'threads', 'watchlist'] as const;
// Consume the shared schema's `.options` so adding a voice flows automatically.
export const AGENT_VOICES = AgentVoiceCanonical.options;
export const ONBOARDING_VARIANTS = ['agent-led', 'form-first', 'hybrid'] as const;

export type Theme = (typeof THEMES)[number];
export type Density = (typeof DENSITIES)[number];
export type TypeScale = (typeof TYPE_SCALES)[number];
export type DarkPalette = (typeof DARK_PALETTES)[number];
export type LightPalette = (typeof LIGHT_PALETTES)[number];
export type RailTab = (typeof RAIL_TABS)[number];
export type AgentVoice = (typeof AGENT_VOICES)[number];
export type OnboardingVariant = (typeof ONBOARDING_VARIANTS)[number];

interface UiState {
  theme: Theme;
  density: Density;
  /** Type-scale axis introduced in v4. `plus2` is the v6 redesign
   *  default (the v6 `TWEAK_DEFAULTS.typeScale`); `plus1` is the v4
   *  scale; `base` is the v3 scale. Lives next to theme/density
   *  because all three are user-controlled display prefs that flip
   *  via /admin/settings. */
  typeScale: TypeScale;

  /** Dark-theme palette (v6). `navy` is the default and resolves via
   *  `:root` tokens; the other three values map to `[data-dark-palette]`
   *  CSS blocks in `packages/design-system/src/tokens/base.css`. Only
   *  effective when `theme` resolves to `midnight`. */
  darkPalette: DarkPalette;

  /** Light-theme palette (v6). `sharp` is the "Classic Lift" default
   *  (resolves via `[data-theme='daylight']` tokens in `daylight.css`);
   *  `cream` is the v5 paperback palette kept as opt-in via
   *  `[data-theme='daylight'][data-light-palette='cream']`. Only
   *  effective when `theme` resolves to `daylight`. */
  lightPalette: LightPalette;

  /** Drives agent tone: coach (default), analyst, operator, quiet (escalations only). */
  agentVoice: AgentVoice;

  /** `agent-led` (chat-first, default), `form-first` (legacy wizard), `hybrid`. */
  onboardingVariant: OnboardingVariant;

  /** Sidebar pin: pinned = always expanded, unpinned = collapse to icons
   *  with hover-expand. CMD+\ toggles. Persisted across reloads. */
  sidePinned: boolean;

  /** Right rail open/collapsed. Persists per-user; the rail is unmounted
   *  on routes that explicitly opt out (`/alerts`, `/agent`). */
  railOpen: boolean;

  /** Rail width in px when open. Drag-to-resize on the leading edge
   *  clamps to [240, 560]. */
  railWidth: number;

  /** Active tab in the rail. PR1 only populates `status`; threads and
   *  watchlist render placeholder shells. */
  railTab: RailTab;

  /** Legacy mirror of `!sidePinned` — kept so existing call sites that
   *  read `sidebarCollapsed` keep working without a coordinated rewrite. */
  sidebarCollapsed: boolean;

  /** AppShell-mounted SplitPane primitive (kept for SplitPane consumers
   *  on EOS pages that haven't been migrated to the rail yet). */
  splitPaneOpen: boolean;
  splitPaneWidth: number;
  splitPaneActiveSectionId: string | undefined;
  paneWidths: Record<string, number>;

  setTheme: (theme: Theme) => void;
  setDensity: (d: Density) => void;
  setTypeScale: (s: TypeScale) => void;
  setDarkPalette: (p: DarkPalette) => void;
  setLightPalette: (p: LightPalette) => void;
  setAgentVoice: (v: AgentVoice) => void;
  setOnboardingVariant: (v: OnboardingVariant) => void;
  toggleTheme: () => void;
  /** Cycles compact → regular → comfy → compact. Replaces the old
   *  2-step toggle. */
  cycleDensity: () => void;
  toggleSidebar: () => void;
  setSidePinned: (v: boolean) => void;
  toggleSidePin: () => void;
  setRailOpen: (v: boolean) => void;
  toggleRail: () => void;
  setRailWidth: (px: number) => void;
  setRailTab: (tab: RailTab) => void;
  toggleSplitPane: () => void;
  setSplitPaneWidth: (px: number) => void;
  setSplitPaneActiveSectionId: (id: string | undefined) => void;
  openSplitPaneToSection: (id: string) => void;
  setPaneWidth: (key: string, px: number) => void;
}

export const SPLIT_PANE_MIN_PX = 280;
export const SPLIT_PANE_MAX_PX = 640;
export const SPLIT_PANE_DEFAULT_PX = 380;

export const RAIL_MIN_PX = 240;
export const RAIL_MAX_PX = 560;
export const RAIL_DEFAULT_PX = 304;

function clampRail(px: number): number {
  return Math.round(Math.max(RAIL_MIN_PX, Math.min(RAIL_MAX_PX, px)));
}

function clampSplit(px: number): number {
  return Math.round(Math.max(SPLIT_PANE_MIN_PX, Math.min(SPLIT_PANE_MAX_PX, px)));
}

export const useUiStore = create<UiState>()(
  persist(
    (set, get) => ({
      theme: 'midnight',
      density: 'comfy',
      typeScale: 'plus2',
      darkPalette: 'navy',
      lightPalette: 'sharp',
      agentVoice: 'coach',
      onboardingVariant: 'agent-led',

      sidePinned: true,
      railOpen: true,
      railWidth: RAIL_DEFAULT_PX,
      railTab: 'status',

      sidebarCollapsed: false,

      splitPaneOpen: true,
      splitPaneWidth: SPLIT_PANE_DEFAULT_PX,
      splitPaneActiveSectionId: undefined,
      paneWidths: {},

      setTheme: (theme) => set({ theme }),
      setDensity: (density) => set({ density }),
      setTypeScale: (typeScale) => set({ typeScale }),
      setDarkPalette: (darkPalette) => set({ darkPalette }),
      setLightPalette: (lightPalette) => set({ lightPalette }),
      setAgentVoice: (agentVoice) => set({ agentVoice }),
      setOnboardingVariant: (onboardingVariant) => set({ onboardingVariant }),

      toggleTheme: () => {
        // Two-state toggle on purpose: the topbar affordance is a quick
        // "I want it light right now" / "I want it dark right now" act,
        // not a way to cycle into auto-follow-OS. `system` is reachable
        // explicitly from /admin/settings → Display. A user on `system`
        // who hits this lands on `midnight` (the v6 canonical default);
        // they re-pick `system` from Settings if they want OS-follow
        // back. Adding `system` to the cycle would force three taps
        // every time anyone just wants the opposite palette.
        const current = get().theme;
        const next: Theme = current === 'midnight' ? 'daylight' : 'midnight';
        set({ theme: next });
      },

      cycleDensity: () => {
        const order: Density[] = ['compact', 'regular', 'comfy'];
        const current = get().density;
        const idx = order.indexOf(current);
        const next = order[(idx + 1) % order.length] ?? 'regular';
        set({ density: next });
      },

      toggleSidebar: () => {
        const next = !get().sidebarCollapsed;
        set({ sidebarCollapsed: next, sidePinned: !next });
      },

      setSidePinned: (v) => set({ sidePinned: v, sidebarCollapsed: !v }),

      toggleSidePin: () => {
        const next = !get().sidePinned;
        set({ sidePinned: next, sidebarCollapsed: !next });
      },

      setRailOpen: (v) => set({ railOpen: v }),
      toggleRail: () => set({ railOpen: !get().railOpen }),

      setRailWidth: (px) => set({ railWidth: clampRail(px) }),

      setRailTab: (tab) => set({ railTab: tab }),

      toggleSplitPane: () => set({ splitPaneOpen: !get().splitPaneOpen }),

      setSplitPaneWidth: (px) => set({ splitPaneWidth: clampSplit(px) }),

      setSplitPaneActiveSectionId: (id) => set({ splitPaneActiveSectionId: id }),

      openSplitPaneToSection: (id) => set({ splitPaneOpen: true, splitPaneActiveSectionId: id }),

      setPaneWidth: (key, px) =>
        set({
          paneWidths: { ...get().paneWidths, [key]: clampSplit(px) },
        }),
    }),
    {
      name: 'leanopx-ui-prefs',
      version: 6,
      // Migrators land additively. Run them in order.
      //   v1 → v2: `parchment` theme renamed to `daylight`; `evidencePane*`
      //            renamed to `splitPane*`.
      //   v2 → v3: density widened to 3-step (`comfortable` → `regular`);
      //            new `sidePinned` / `railOpen` / `railWidth` / `railTab`
      //            keys appear with their defaults if not present.
      //   v3 → v4: new `typeScale: 'base' | 'plus1' | 'plus2'` axis added.
      //            Legacy users (no key) land on `plus1` so they see the
      //            redesign bump on first load; density preserves whatever
      //            they had in v3 (no silent flip to `comfy`).
      //   v4 → v5: new `agentVoice` + `onboardingVariant` axes; defaults
      //            `coach` + `agent-led`.
      //   v5 → v6: new `darkPalette` + `lightPalette` axes; defaults
      //            `navy` + `sharp` (v6 redesign — issue #114 Phase A).
      //            Existing visitors who already persisted a typeScale
      //            in v4/v5 (`plus1` was the prior default) keep their
      //            choice — only fresh visitors get the new `plus2`
      //            default. The migrator does NOT silently flip
      //            `plus1` → `plus2`; that would override an explicit
      //            user pref.
      migrate: (persisted, fromVersion) => persistMigrate(persisted, fromVersion),
    },
  ),
);

/**
 * Zustand persist-middleware migrator. Extracted from the store config
 * so the v5→v6 (and earlier) migration logic is unit-testable without
 * spinning up the full persist pipeline. Idempotent — each `if` branch
 * is gated on `fromVersion < N`, so re-running on already-migrated
 * state is a no-op.
 *
 * Versions:
 *   1 → 2: theme rename (`parchment` → `daylight`), evidencePane fields
 *          renamed to splitPane.
 *   2 → 3: density widened to 3-step (`compact / regular / comfy`),
 *          sidePinned + rail* fields introduced.
 *   3 → 4: typeScale axis introduced (legacy users land on `plus1`).
 *   4 → 5: agentVoice + onboardingVariant introduced.
 *   5 → 6 (this PR, issue #114): darkPalette + lightPalette added.
 *          Legacy users (no key) land on the v6 defaults (`navy` /
 *          `sharp`) — which resolve via the unconditional `:root` /
 *          `[data-theme='daylight']` token blocks, so this is a no-op
 *          visual change for anyone without a palette preference.
 *          `typeScale` is intentionally NOT touched — v5 users on
 *          `plus1` stay on `plus1`; only fresh visitors get `plus2`.
 *          Out-of-set persisted palette values fall back to defaults.
 */
export function persistMigrate(persisted: unknown, fromVersion: number): UiState {
  if (!persisted || typeof persisted !== 'object') {
    // Corrupt blob (null, primitive, array) — return an empty object so
    // Zustand's `persist` middleware merges its own store defaults on
    // top. Returning `null` would crash the hydration step
    // (`Object.assign(state, migrated)` throws on a null `migrated`),
    // which was the failure mode pre-v6.
    return {} as UiState;
  }
  let state = persisted as Record<string, unknown>;

  if (fromVersion < 2) {
    const { theme, evidencePaneOpen, evidencePaneWidth, ...rest } = state as Record<
      string,
      unknown
    > & {
      theme?: string;
      evidencePaneOpen?: boolean;
      evidencePaneWidth?: number;
    };
    state = {
      ...rest,
      theme: theme === 'parchment' ? 'daylight' : theme,
      splitPaneOpen: evidencePaneOpen ?? true,
      splitPaneWidth: evidencePaneWidth ?? SPLIT_PANE_DEFAULT_PX,
      splitPaneActiveSectionId: undefined,
    };
  }

  if (fromVersion < 3) {
    const rawDensity = state['density'];
    const density: Density =
      rawDensity === 'compact' ? 'compact' : rawDensity === 'comfy' ? 'comfy' : 'regular';
    // sidePinned default mirrors the v2 sidebarCollapsed preference —
    // a user who explicitly collapsed the sidebar pre-v3 expects it
    // to stay collapsed, not snap back open on first render.
    state = {
      ...state,
      density,
      sidePinned: state['sidePinned'] ?? !(state['sidebarCollapsed'] ?? false),
      railOpen: state['railOpen'] ?? true,
      railWidth:
        typeof state['railWidth'] === 'number'
          ? clampRail(state['railWidth'] as number)
          : RAIL_DEFAULT_PX,
      railTab: state['railTab'] ?? 'status',
      sidebarCollapsed:
        typeof state['sidebarCollapsed'] === 'boolean' ? state['sidebarCollapsed'] : false,
    };
  }

  if (fromVersion < 4) {
    // v4 adds the typeScale axis. Land legacy users on the v4 redesign
    // default (`plus1`) so the bump is visible on first load. v6
    // bumped the fresh-visitor default again to `plus2`, but the
    // fromVersion<6 branch deliberately doesn't touch typeScale so
    // returning v5 users stay on `plus1`.
    const raw = state['typeScale'];
    const typeScale: TypeScale =
      raw === 'base' || raw === 'plus1' || raw === 'plus2' ? raw : 'plus1';
    state = { ...state, typeScale };
  }

  if (fromVersion < 5) {
    const rawVoice = state['agentVoice'];
    const agentVoice: AgentVoice = (AGENT_VOICES as readonly string[]).includes(rawVoice as string)
      ? (rawVoice as AgentVoice)
      : 'coach';
    const rawVariant = state['onboardingVariant'];
    const onboardingVariant: OnboardingVariant = (
      ONBOARDING_VARIANTS as readonly string[]
    ).includes(rawVariant as string)
      ? (rawVariant as OnboardingVariant)
      : 'agent-led';
    state = { ...state, agentVoice, onboardingVariant };
  }

  if (fromVersion < 6) {
    const rawDark = state['darkPalette'];
    const darkPalette: DarkPalette = (DARK_PALETTES as readonly string[]).includes(
      rawDark as string,
    )
      ? (rawDark as DarkPalette)
      : 'navy';
    const rawLight = state['lightPalette'];
    const lightPalette: LightPalette = (LIGHT_PALETTES as readonly string[]).includes(
      rawLight as string,
    )
      ? (rawLight as LightPalette)
      : 'sharp';
    state = { ...state, darkPalette, lightPalette };
  }

  return state as unknown as UiState;
}

/** Apply UI store state to <html> + <body>. Idempotent — safe to call on
 *  every store change. The split between html (theme class) and body
 *  (data-density) intentionally puts density on body so prototype-style
 *  selectors `body[data-density='compact']` resolve naturally.
 *
 *  v4: also writes `data-theme` + `data-type-scale` on <html>. The CSS
 *  selectors in `apps/web/src/styles/index.css` match either the legacy
 *  `.midnight` / `.daylight` class or the new attribute, so existing
 *  selectors keep resolving and v4 design-system primitives can use the
 *  attribute form the prototype shipped with. */
export function applyUiToDom(state: UiState) {
  const html = document.documentElement;
  // Density lives on body in v3; keep a mirror on html so the inline
  // pre-hydrate script (which runs before body exists) can still set
  // it without flicker, then the runtime mirror keeps both in sync.
  if (typeof document.body !== 'undefined' && document.body) {
    document.body.setAttribute('data-density', state.density);
  }
  html.setAttribute('data-density', state.density);
  html.setAttribute('data-type-scale', state.typeScale);

  // Resolve `system` to a concrete midnight/daylight value BEFORE we
  // decide which palette axis to write. The palette attribute must
  // only ride alongside its matching theme — writing
  // `data-dark-palette='forest'` while in daylight is a noop today
  // (the CSS scopes to `[data-theme='midnight']`) but would silently
  // leak if a future palette block dropped the theme scope, and is
  // confusing to anyone debugging the DOM.
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  const useMidnight = state.theme === 'midnight' || (state.theme === 'system' && prefersDark);

  // Palette axes (v6, issue #114). Defaults (`navy` / `sharp`) live in
  // the unconditional `:root` / `[data-theme='daylight']` blocks, so we
  // only write `data-dark-palette` / `data-light-palette` when the user
  // has opted off the default. Each attribute is also scoped to the
  // resolved theme (dark palette only when midnight, light palette only
  // when daylight) AND validated against the closed sets so a corrupted
  // persisted value can't smuggle an arbitrary attribute onto <html>.
  // Mirrors the pre-hydrate script in `apps/web/index.html`.
  if (useMidnight && state.darkPalette !== 'navy' && DARK_PALETTES.includes(state.darkPalette)) {
    html.setAttribute('data-dark-palette', state.darkPalette);
  } else {
    html.removeAttribute('data-dark-palette');
  }
  if (
    !useMidnight &&
    state.lightPalette !== 'sharp' &&
    LIGHT_PALETTES.includes(state.lightPalette)
  ) {
    html.setAttribute('data-light-palette', state.lightPalette);
  } else {
    html.removeAttribute('data-light-palette');
  }

  html.classList.toggle('midnight', useMidnight);
  html.classList.toggle('daylight', !useMidnight);
  // `data-theme` mirrors the class so v4 attribute-selector CSS
  // (`[data-theme='daylight']`) resolves. `system` always resolves to a
  // concrete value here — never write `system` to the DOM.
  html.setAttribute('data-theme', useMidnight ? 'midnight' : 'daylight');

  // Rail width as a CSS custom property so the shell grid track flips
  // without a re-render. Sidebar collapse is handled separately via the
  // `.is-side-collapsed` class on `.portal-shell`, which swaps the
  // `--side-w-eff` track between `--side-w` and `--side-w-collapsed` —
  // see `apps/web/src/styles/index.css` (`.portal-shell.is-side-collapsed`).
  html.style.setProperty('--rail-w-eff', `${state.railWidth}px`);
}

// `index.html` contains an inline pre-hydrate script that reads
// `leanopx-ui-prefs` from localStorage and writes theme/density to
// <html> BEFORE any React bundle loads — so there's no theme flash on
// first paint. Once Zustand finishes its own async hydration we simply
// keep the DOM in sync with any runtime changes (theme toggle, etc).
if (typeof document !== 'undefined') {
  useUiStore.persist.onFinishHydration((state) => {
    applyUiToDom(state);
  });
  useUiStore.subscribe((state) => {
    applyUiToDom(state);
  });
}
